import { MobileNotificationsFeed } from "@/components/MobileNotificationsFeed";
import UpcomingDeadlinesWidget from "@/components/UpcomingDeadlinesWidget";
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Buildings,
  Scroll,
  Certificate,
  MagnifyingGlass,
  ChartLine,
  Files,
  Bell,
  ClockCounterClockwise,
  Handshake,
  Ruler,
  Question,
  Kanban,
  PenNib,
  Timer,
} from "@phosphor-icons/react";
import { runCategoryTour } from "./tour";

// Iter208.99b — Hook riusabile per KPI dashboard categoria (Tecnica/Notaio).
// Endpoint: GET /api/dashboard/category-kpis (auth required, professional only).
function useCategoryKpis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/dashboard/category-kpis")
      .then((r) => { if (mounted) { setData(r.data); setError(null); } })
      .catch((e) => { if (mounted) setError(e?.response?.data?.detail || "Errore caricamento KPI"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);
  return { data, loading, error };
}

function fmtAvgDays(v) {
  if (v == null) return "—";
  if (v < 1) return "<1 g";
  return `${v} g`;
}

/**
 * Componenti dashboard specifiche per categoria professionale.
 *
 * Struttura comune (iter50): tutti i componenti mostrano un header con
 * saluto personalizzato + una griglia di "Placeholder card" pensata come
 * scheletro pronto per essere popolato in iterazioni successive con i dati
 * reali (KPI, grafici, liste attività).
 *
 * Ogni card usa `data-testid` prevedibili così i test frontend possono
 * verificare che venga renderizzata la dashboard corretta.
 */

function DashboardShell({ user, eyebrow, title, subtitle, testId, tourType, children }) {
  const { user: authUser, setUser } = useAuth();
  const triggered = useRef(false);

  const persistTourDone = async () => {
    try {
      await api.patch("/auth/profile", { tour_completed_dashboard: true });
      if (setUser && authUser) setUser({ ...authUser, tour_completed_dashboard: true });
    } catch {
      /* best-effort */
    }
  };

  useEffect(() => {
    if (triggered.current) return;
    if (!tourType || !user) return;
    if (user.tour_completed_dashboard) return;
    triggered.current = true;
    runCategoryTour(tourType, { onFinish: persistTourDone });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tour_completed_dashboard, tourType]);

  const replayTour = () => {
    triggered.current = true;
    runCategoryTour(tourType, { onFinish: persistTourDone });
  };

  return (
    <div data-testid={testId} className="fade-up">
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">{eyebrow}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
            Ciao {user?.first_name || "Professionista"} — {title}
          </h1>
          {subtitle && (
            <p className="text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>
          )}
        </div>
        {tourType && (
          <Button
            variant="outline"
            size="sm"
            onClick={replayTour}
            data-testid={`${testId}-replay-tour`}
            className="shrink-0"
          >
            <Question size={14} className="mr-1" weight="bold" /> Rifai il tour
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {children}
      </div>
      <div className="mt-6">
        <UpcomingDeadlinesWidget />
        <MobileNotificationsFeed />
      </div>
    </div>
  );
}

function PlaceholderCard({ icon: Icon, title, subtitle, hint, color = "#6D28D9", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-dashed bg-white p-3 md:p-5 hover:shadow-sm transition-shadow min-w-0"
    >
      <div className="flex items-start gap-2 md:gap-3 mb-2 md:mb-3">
        {Icon && (
          <div
            className="w-8 h-8 md:w-10 md:h-10 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${color}14`, color }}
          >
            <Icon size={20} weight="duotone" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-xs md:text-sm text-slate-800 truncate">{title}</div>
          {subtitle && (
            <div className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{subtitle}</div>
          )}
        </div>
      </div>
      <div className="text-[10px] md:text-[11px] uppercase tracking-wider text-slate-400 mb-1">Prossimamente</div>
      <div className="text-xs md:text-sm text-slate-600 leading-snug">{hint}</div>
    </div>
  );
}

/** DataCard: versione "popolata" della PlaceholderCard usata per i KPI reali.
 *  Mostra un valore primario grande + testo secondario + hint (multi-riga). */
function DataCard({ icon: Icon, title, primary, secondary, hint, color = "#6D28D9", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border bg-white p-3 md:p-5 hover:shadow-sm transition-shadow min-w-0"
    >
      <div className="flex items-start gap-2 md:gap-3 mb-2">
        {Icon && (
          <div
            className="w-8 h-8 md:w-10 md:h-10 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${color}14`, color }}
          >
            <Icon size={20} weight="duotone" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-xs md:text-sm text-slate-800 truncate">{title}</div>
          {secondary && <div className="text-[10px] md:text-xs text-muted-foreground mt-0.5 truncate">{secondary}</div>}
        </div>
      </div>
      <div className="mt-1 md:mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight leading-none break-words" style={{ color }}>
        {primary}
      </div>
      {hint && (
        <div className="mt-1 md:mt-2 text-[10px] md:text-xs text-slate-500 leading-snug whitespace-pre-line">
          {hint}
        </div>
      )}
    </div>
  );
}

// ==================== DashboardTecnica ====================
export function DashboardTecnica() {
  const { user } = useAuth();
  const { data: kpis, loading } = useCategoryKpis();
  return (
    <DashboardShell
      user={user}
      eyebrow="Dashboard Studio Tecnico"
      title="pratiche edilizie in un colpo d'occhio"
      subtitle="Monitora sanatorie, SCIA, permessi di costruire e sopralluoghi in un'unica vista dedicata all'ambito tecnico."
      testId="dashboard-tecnica"
      tourType="tecnica"
    >
      <DataCard
        icon={Kanban}
        title="Pratiche in corso"
        secondary="Tutte le attive del tuo studio"
        primary={loading ? "…" : (kpis?.open_practices ?? 0)}
        hint="Include pratiche appena create, in lavorazione e in attesa di consegna."
        color="#4F46E5"
        testId="dashboard-tecnica-open-practices"
      />
      <DataCard
        icon={PenNib}
        title="Atti firmati"
        secondary="Documenti con firma OTP"
        primary={loading ? "…" : (kpis?.signed_documents ?? 0)}
        hint="Numero cumulativo di documenti firmati digitalmente dai clienti finali sulle tue pratiche."
        color="#166534"
        testId="dashboard-tecnica-signed"
      />
      <DataCard
        icon={Timer}
        title="Tempo medio di completamento"
        secondary={kpis?.completed_total ? `Su ${kpis.completed_total} pratiche chiuse` : "Nessuna pratica ancora chiusa"}
        primary={loading ? "…" : fmtAvgDays(kpis?.avg_completion_days)}
        hint="Media dei giorni tra creazione e chiusura delle pratiche completate."
        color="#B45309"
        testId="dashboard-tecnica-avg-days"
      />
      <PlaceholderCard
        icon={Ruler}
        title="Sopralluoghi programmati"
        subtitle="Prossimi 30 giorni"
        hint="Timeline dei sopralluoghi con esito rilievi e allegati preparati per la relazione."
        color="#166534"
        testId="dashboard-tecnica-sopralluoghi"
      />
      <PlaceholderCard
        icon={ClockCounterClockwise}
        title="Timeline documentale"
        subtitle="Ultimi 60 giorni"
        hint="Cronologia delle richieste di integrazione documenti e risposte del cliente/committente."
        color="#7C3AED"
        testId="dashboard-tecnica-timeline"
      />
      <PlaceholderCard
        icon={Bell}
        title="Notifiche pratiche urgenti"
        subtitle="Deadline sotto 72h"
        hint="Radar sulle pratiche con scadenza imminente per interventi prioritari."
        color="#991B1B"
        testId="dashboard-tecnica-urgenti"
      />
    </DashboardShell>
  );
}

// ==================== DashboardNotaio ====================
export function DashboardNotaio() {
  const { user } = useAuth();
  const { data: kpis, loading } = useCategoryKpis();
  return (
    <DashboardShell
      user={user}
      eyebrow="Dashboard Studio Notarile"
      title="atti e istruttorie sotto controllo"
      subtitle="Cruscotto dedicato alla gestione degli atti di compravendita, delle donazioni e dei contratti in attesa di stipula."
      testId="dashboard-notaio"
      tourType="notaio"
    >
      <DataCard
        icon={Kanban}
        title="Pratiche in corso"
        secondary="Istruttorie attive dello studio"
        primary={loading ? "…" : (kpis?.open_practices ?? 0)}
        hint="Atti in attesa di stipula, visure e adempimenti fiscali ancora aperti."
        color="#6D28D9"
        testId="dashboard-notaio-open-practices"
      />
      <DataCard
        icon={PenNib}
        title="Atti firmati"
        secondary="Con firma digitale OTP"
        primary={loading ? "…" : (kpis?.signed_documents ?? 0)}
        hint="Numero cumulativo di documenti firmati digitalmente dai clienti finali sulle tue pratiche."
        color="#166534"
        testId="dashboard-notaio-signed"
      />
      <DataCard
        icon={Timer}
        title="Tempo medio di completamento"
        secondary={kpis?.completed_total ? `Su ${kpis.completed_total} pratiche chiuse` : "Nessuna pratica ancora chiusa"}
        primary={loading ? "…" : fmtAvgDays(kpis?.avg_completion_days)}
        hint="Media dei giorni tra creazione e chiusura delle pratiche completate."
        color="#B45309"
        testId="dashboard-notaio-avg-days"
      />
      <PlaceholderCard
        icon={MagnifyingGlass}
        title="Visure & ispezioni"
        subtitle="Coda operativa"
        hint="Stato delle visure ipocatastali richieste + ispezioni ancora da evadere."
        color="#4F46E5"
        testId="dashboard-notaio-visure"
      />
      <PlaceholderCard
        icon={ClockCounterClockwise}
        title="Scadenze fiscali"
        subtitle="Registrazione / trascrizione"
        hint="Reminder scadenze per registrazione telematica atti e trascrizioni presso i registri immobiliari."
        color="#B45309"
        testId="dashboard-notaio-scadenze"
      />
      <PlaceholderCard
        icon={Files}
        title="Fascicoli aperti"
        subtitle="Cronologia interventi"
        hint="Elenco dei fascicoli attivi con avanzamento della raccolta documentale."
        color="#0F766E"
        testId="dashboard-notaio-fascicoli"
      />
    </DashboardShell>
  );
}

// ==================== DashboardAgenzia ====================
export function DashboardAgenzia() {
  const { user } = useAuth();
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/dashboard/agenzia").then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  const fmt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
  const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
  return (
    <DashboardShell
      user={user}
      eyebrow="Dashboard Agenzia Immobiliare"
      title="vendite, listino e team in un colpo d'occhio"
      subtitle="Cruscotto ottimizzato per agenzie immobiliari: pipeline vendite, analisi listino e performance del team commerciale."
      testId="dashboard-agenzia"
      tourType="agenzia"
    >
      <DataCard
        icon={ChartLine}
        title="Servizi consegnati (mese)"
        primary={data ? fmt(data.vendite.completed_this_month) : "—"}
        secondary={data ? `Fatturato ${eur(data.vendite.revenue_this_month)}` : "Caricamento…"}
        hint={data && data.vendite.monthly.length > 0
          ? `Ultimi 12 mesi: ${data.vendite.monthly.reduce((s, m) => s + m.count, 0)} consegne totali`
          : "Nessun servizio completato nell'ultimo anno."}
        color="#166534"
        testId="dashboard-agenzia-vendite"
      />
      <DataCard
        icon={Buildings}
        title="Immobili in gestione"
        primary={data ? fmt(data.listino.attivi) : "—"}
        secondary={data ? `+ ${fmt(data.listino.archiviati)} archiviati` : "Caricamento…"}
        hint="Immobili attivi nel tuo portafoglio. Vai a Immobili per gestire."
        color="#4F46E5"
        testId="dashboard-agenzia-listino"
      />
      <DataCard
        icon={Handshake}
        title="Servizi in lavorazione"
        primary={data ? fmt(data.trattative_aperte) : "—"}
        secondary="Pratiche aperte"
        hint="Servizi tecnici attualmente in corso su immobili del tuo team."
        color="#B45309"
        testId="dashboard-agenzia-trattative"
      />
      <DataCard
        icon={MagnifyingGlass}
        title="Lead & richieste (90gg)"
        primary={data ? fmt(data.lead_last_90d) : "—"}
        secondary="Form submissions ricevute"
        hint="Contatti arrivati dai tuoi form pubblici negli ultimi 90 giorni."
        color="#0F766E"
        testId="dashboard-agenzia-lead"
      />
      <DataCard
        icon={Certificate}
        title="Fatturato mese"
        primary={data ? eur(data.vendite.revenue_this_month) : "—"}
        secondary={data ? `${fmt(data.vendite.completed_this_month)} consegne` : ""}
        hint="Somma dei prezzi di acquisto pratiche completate nel mese corrente."
        color="#7C3AED"
        testId="dashboard-agenzia-provvigioni"
      />
      <DataCard
        icon={Bell}
        title="Alert scadenza (72h)"
        primary={data ? fmt((data.alerts || []).length) : "—"}
        secondary="Pratiche prossime alla deadline"
        hint={data && data.alerts.length > 0
          ? data.alerts.slice(0, 3).map(a => `• ${a.service_name} — ${a.address}`).join("\n")
          : "Nessuna pratica urgente."}
        color="#991B1B"
        testId="dashboard-agenzia-alert"
      />
    </DashboardShell>
  );
}
